import { Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Providers from './Providers';
import ErrorBoundary from './components/ErrorBoundary';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import SiteChrome from './components/layout/SiteChrome';
import { PageSkeleton } from './components/skeletons/PageSkeleton';
import { EmbroideryDefs } from './components/design/EmbroideryFilter';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Lazy-load pages for code-splitting. lazyWithRetry adds chunk-load retry +
// a one-shot reload on stale-build chunk 404s.
const HomePage = lazyWithRetry(() => import('./pages/home/HomePage'));
const LoginPage = lazyWithRetry(() => import('./pages/auth/LoginPage'));
const VerifyPage = lazyWithRetry(() => import('./pages/auth/VerifyPage'));
const AccountPage = lazyWithRetry(() => import('./pages/account/AccountPage'));
const AddressesPage = lazyWithRetry(() => import('./pages/account/AddressesPage'));
const DesignsPage = lazyWithRetry(() => import('./pages/account/DesignsPage'));
const LoyaltyPage = lazyWithRetry(() => import('./pages/account/LoyaltyPage'));
const NotificationsPage = lazyWithRetry(() => import('./pages/account/NotificationsPage'));
const WishlistPage = lazyWithRetry(() => import('./pages/account/WishlistPage'));
const CartPage = lazyWithRetry(() => import('./pages/cart/CartPage'));
const CategoriesPage = lazyWithRetry(() => import('./pages/categories/CategoriesPage'));
const CategoryDetailPage = lazyWithRetry(() => import('./pages/categories/CategoryDetailPage'));
const BusinessCardsLanding = lazyWithRetry(() => import('./pages/categories/BusinessCardsLanding'));
const CheckoutPage = lazyWithRetry(() => import('./pages/checkout/CheckoutPage'));
const EditorPage = lazyWithRetry(() => import('./pages/design/EditorPage'));
const DesignStartPage = lazyWithRetry(() => import('./pages/design/DesignStartPage'));
const DesignHubPage = lazyWithRetry(() => import('./pages/design/DesignHubPage'));
const HelpPage = lazyWithRetry(() => import('./pages/help/HelpPage'));
const OrdersPage = lazyWithRetry(() => import('./pages/orders/OrdersPage'));
const OrderConfirmationPage = lazyWithRetry(() => import('./pages/orders/OrderConfirmationPage'));
const OrderDetailPage = lazyWithRetry(() => import('./pages/orders/OrderDetailPage'));
const ReprintPaymentPage = lazyWithRetry(() => import('./pages/pay/ReprintPaymentPage'));
const ProductDetailPage = lazyWithRetry(() => import('./pages/products/ProductDetailPage'));
const CmsPage = lazyWithRetry(() => import('./pages/cms/CmsPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));

// Layouts
const AuthLayout = lazyWithRetry(() => import('./layouts/AuthLayout'));
const AccountLayout = lazyWithRetry(() => import('./layouts/AccountLayout'));
const CartLayout = lazyWithRetry(() => import('./layouts/CartLayout'));
const CheckoutLayout = lazyWithRetry(() => import('./layouts/CheckoutLayout'));
const OrdersLayout = lazyWithRetry(() => import('./layouts/OrdersLayout'));
const HelpLayout = lazyWithRetry(() => import('./layouts/HelpLayout'));
const PayLayout = lazyWithRetry(() => import('./layouts/PayLayout'));
const EditorLayout = lazyWithRetry(() => import('./layouts/EditorLayout'));

const APP_NAME = import.meta.env.VITE_APP_NAME || 'EsyPrints';
const APP_TAGLINE = `${APP_NAME} - Your Prints, Delivered in Hours`;

// Wrap a route element in a per-route ErrorBoundary so a crash on one screen
// doesn't blank the whole app. `name` is sent to Sentry as `extra.routeName`.
function guard(name: string, element: ReactElement): ReactElement {
  return <RouteErrorBoundary routeName={name}>{element}</RouteErrorBoundary>;
}

// Root layout — single Suspense + provider tree shared by every route.
// Using a RootLayout + data router enables useBlocker (needed for the editor's
// unsaved-changes guard on intra-app navigation).
function RootLayout() {
  return (
    <ErrorBoundary>
      <Helmet>
        <title>{APP_TAGLINE}</title>
        <meta property="og:site_name" content={APP_NAME} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>
      <Providers>
        <EmbroideryDefs />
        <SiteChrome>
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </SiteChrome>
      </Providers>
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { index: true, element: guard('home', <HomePage />) },

      {
        element: guard('auth-layout', <AuthLayout />),
        children: [
          { path: 'login', element: guard('login', <LoginPage />) },
          { path: 'verify', element: guard('verify', <VerifyPage />) },
        ],
      },

      {
        element: guard('account-layout', <AccountLayout />),
        children: [
          { path: 'account', element: guard('account', <AccountPage />) },
          { path: 'account/addresses', element: guard('account-addresses', <AddressesPage />) },
          { path: 'account/designs', element: guard('account-designs', <DesignsPage />) },
          { path: 'account/loyalty', element: guard('account-loyalty', <LoyaltyPage />) },
          { path: 'account/notifications', element: guard('account-notifications', <NotificationsPage />) },
          { path: 'account/wishlist', element: guard('account-wishlist', <WishlistPage />) },
        ],
      },

      {
        element: guard('cart-layout', <CartLayout />),
        children: [{ path: 'cart', element: guard('cart', <CartPage />) }],
      },

      { path: 'categories', element: guard('categories', <CategoriesPage />) },
      { path: 'business-cards', element: guard('business-cards', <BusinessCardsLanding />) },
      { path: 'categories/:slug', element: guard('category-detail', <CategoryDetailPage />) },

      {
        element: guard('checkout-layout', <CheckoutLayout />),
        children: [{ path: 'checkout', element: guard('checkout', <CheckoutPage />) }],
      },

      {
        element: guard('editor-layout', <EditorLayout />),
        children: [
          { path: 'design/editor/:designId', element: guard('editor', <EditorPage />) },
        ],
      },
      // Bare /design (the mobile bottom-nav "Design" tab) — product picker
      // for the design studio. Was previously an unrouted 404.
      { path: 'design', element: guard('design-hub', <DesignHubPage />) },
      { path: 'design/:productSlug', element: guard('design-start', <DesignStartPage />) },

      {
        element: guard('help-layout', <HelpLayout />),
        children: [{ path: 'help', element: guard('help', <HelpPage />) }],
      },

      {
        element: guard('orders-layout', <OrdersLayout />),
        children: [
          { path: 'orders', element: guard('orders', <OrdersPage />) },
          { path: 'orders/confirmation', element: guard('order-confirmation', <OrderConfirmationPage />) },
          { path: 'orders/:orderNumber', element: guard('order-detail', <OrderDetailPage />) },
        ],
      },

      {
        element: guard('pay-layout', <PayLayout />),
        children: [
          { path: 'pay/reprint/:orderNumber', element: guard('pay-reprint', <ReprintPaymentPage />) },
        ],
      },

      { path: 'products/:slug', element: guard('product-detail', <ProductDetailPage />) },

      // Admin-managed CMS pages (About, Terms, Privacy, ...) live at /:slug.
      // Static routes above always win over this dynamic segment; unknown
      // slugs 404 inside CmsPage itself.
      { path: ':slug', element: guard('cms-page', <CmsPage />) },

      { path: '*', element: guard('not-found', <NotFoundPage />) },
    ],
  },
]);
